import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { message } from "antd";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { useDebouncedSearchQuery } from "../../../hooks/useDebouncedSearchQuery";
import {
  useGetDuplicateIdentityGroups,
  GET_DUPLICATE_IDENTITY_GROUPS_QUERY_KEY,
} from "../../../queries/duplicate-identity/useGetDuplicateIdentityGroups";
import { useGetDuplicateIdentityGroupDetail } from "../../../queries/duplicate-identity/useGetDuplicateIdentityGroupDetail";
import { usePatchDuplicateIdentityGroup } from "../../../queries/duplicate-identity/usePatchDuplicateIdentityGroup";
import { usePostDuplicateIdentityMergeDryRun } from "../../../queries/duplicate-identity/usePostDuplicateIdentityMergeDryRun";
import type {
  DuplicateIdentitySearchBy,
  DuplicateIdentityStatus,
} from "../../../queries/duplicate-identity/types";

export type DuplicateIdentityReviewTab = "PENDING" | "ACTIONED";

const ACTIONED_STATUSES: DuplicateIdentityStatus[] = [
  "support_contacted",
  "escalated",
  "merged",
  "ignored",
];

type DuplicateIdentityReviewContextValue = {
  selectedTab: DuplicateIdentityReviewTab;
  setSelectedTab: (tab: DuplicateIdentityReviewTab) => void;
  searchBy: DuplicateIdentitySearchBy | undefined;
  setSearchBy: (value: DuplicateIdentitySearchBy | undefined) => void;
  searchText: string;
  setSearchText: (value: string) => void;
  tenantFilter: string;
  setTenantFilter: (value: string) => void;
  pageSize: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  groups: ReturnType<typeof useGetDuplicateIdentityGroups>["data"];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  selectedGroupId: string | null;
  openDetail: (groupId: string) => void;
  closeDetail: () => void;
  detailQuery: ReturnType<typeof useGetDuplicateIdentityGroupDetail>;
  notesDraft: string;
  setNotesDraft: (value: string) => void;
  handleMarkSupportContacted: () => Promise<void>;
  handleEscalate: () => Promise<void>;
  handleIgnore: () => Promise<void>;
  handleMergeDryRun: () => Promise<void>;
  isUpdating: boolean;
  isDryRunPending: boolean;
  mergeDryRunCorrelationId: string | null;
};

const DuplicateIdentityReviewContext =
  createContext<DuplicateIdentityReviewContextValue | null>(null);

export function DuplicateIdentityReviewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedTab, setSelectedTabState] =
    useState<DuplicateIdentityReviewTab>("PENDING");
  const [searchBy, setSearchByState] = useState<
    DuplicateIdentitySearchBy | undefined
  >("user_id");
  const [searchText, setSearchText] = useState("");
  const [tenantFilter, setTenantFilterState] = useState("");
  // Fixed — the DynamoDB-backed API is single-direction cursor-paginated, so the
  // page size can't be changed mid-pagination without invalidating every cursor
  // collected so far. There is currently no UI to change it.
  const pageSize = 20;
  const [currentPage, setCurrentPageState] = useState(1);
  // Cursor for each page already reached, keyed by page number (1 => null, the
  // first page needs none). The backend only supports "give me the page after
  // cursor X" — there is no way to seek to an arbitrary page — so navigation is
  // restricted to next/previous and every cursor is captured as we pass through
  // it, letting "previous" replay a cursor we already have instead of guessing.
  const [pageCursors, setPageCursors] = useState<Record<number, string | null>>({
    1: null,
  });
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [mergeDryRunCorrelationId, setMergeDryRunCorrelationId] = useState<
    string | null
  >(null);

  const { effectiveSearchQuery } = useDebouncedSearchQuery(searchText, {
    delayMs: 300,
  });

  const resetPagination = () => {
    setCurrentPageState(1);
    setPageCursors({ 1: null });
    setHasNextPage(false);
  };

  const setSelectedTab = (tab: DuplicateIdentityReviewTab) => {
    setSelectedTabState(tab);
    resetPagination();
  };

  const setSearchBy = (value: DuplicateIdentitySearchBy | undefined) => {
    setSearchByState(value);
    resetPagination();
  };

  const setTenantFilter = (value: string) => {
    setTenantFilterState(value);
    resetPagination();
  };

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSearchQuery]);

  // `searchBy`/`effectiveSearchQuery` aren't forwarded to the request: neither
  // the v1 backend this UI originally shipped against, nor the current
  // admin_panel_v2 endpoint, implement a duplicate-identity text search (the
  // controls are kept — see `DuplicateIdentityReview.tsx` — but wiring a real
  // backend search is a separate follow-up).
  const listFilters = useMemo(
    () => ({
      cursor: pageCursors[currentPage] ?? null,
      size: pageSize,
      tenant: tenantFilter.trim() || undefined,
      ...(selectedTab === "PENDING"
        ? { status: "pending_review" as DuplicateIdentityStatus }
        : { statuses: ACTIONED_STATUSES }),
    }),
    [currentPage, pageCursors, tenantFilter, selectedTab],
  );

  const {
    data: groups,
    isLoading,
    isFetching,
    isError,
  } = useGetDuplicateIdentityGroups(listFilters);

  const goToNextPage = () => {
    // `hasNextPage` only reflects the response for the page we're currently on —
    // a click landing between "that response arrived" and "the next page's
    // response arrives" would advance past a page whose cursor isn't captured
    // yet (double-clicking Next faster than the round-trip). The query uses
    // `placeholderData: keepPreviousData`, so once the first page has loaded
    // `isLoading` (== isPending && isFetching) stays false on every subsequent
    // page-to-page fetch — only `isFetching` reflects an in-flight navigation
    // fetch, so that's what has to gate this, not `isLoading`. The table also
    // disables the controls on `isFetching` for the same reason.
    if (!hasNextPage || isFetching) return;
    setCurrentPageState((page) => page + 1);
  };

  const goToPreviousPage = () => {
    if (isFetching) return;
    setCurrentPageState((page) => Math.max(1, page - 1));
  };

  useEffect(() => {
    const nextKey = groups?.next_page ?? null;
    setHasNextPage(Boolean(nextKey));
    if (nextKey) {
      setPageCursors((prev) =>
        prev[currentPage + 1] === nextKey
          ? prev
          : { ...prev, [currentPage + 1]: nextKey },
      );
    }
    // currentPage is intentionally excluded: this effect reacts to a new `groups`
    // response landing, and re-derives which page that response was for from the
    // closure at fetch time — adding it here would also re-run on pure page
    // navigation before the new data arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const detailQuery = useGetDuplicateIdentityGroupDetail(
    selectedGroupId,
    Boolean(selectedGroupId),
  );

  useEffect(() => {
    if (detailQuery.data?.notes != null) {
      setNotesDraft(detailQuery.data.notes);
    } else if (selectedGroupId) {
      setNotesDraft("");
    }
  }, [detailQuery.data?.notes, selectedGroupId]);

  const { mutateAsync: patchGroup, isPending: isUpdating } =
    usePatchDuplicateIdentityGroup();
  const { mutateAsync: triggerDryRun, isPending: isDryRunPending } =
    usePostDuplicateIdentityMergeDryRun();

  const openDetail = (groupId: string) => {
    setSelectedGroupId(groupId);
    setMergeDryRunCorrelationId(null);
  };

  const closeDetail = () => {
    setSelectedGroupId(null);
    setMergeDryRunCorrelationId(null);
  };

  const patchWithStatus = async (
    status: "support_contacted" | "escalated" | "ignored",
  ) => {
    if (!selectedGroupId) return;
    try {
      await patchGroup({
        group_id: selectedGroupId,
        status,
        notes: notesDraft.trim() || undefined,
      });
      message.success(AppStrings.KYC_DUPLICATE_IDENTITY_UPDATE_SUCCESS);
      closeDetail();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.KYC_DUPLICATE_IDENTITY_UPDATE_ERROR),
      );
      throw error;
    }
  };

  const handleMarkSupportContacted = () => patchWithStatus("support_contacted");
  const handleEscalate = () => patchWithStatus("escalated");
  const handleIgnore = () => patchWithStatus("ignored");

  const handleMergeDryRun = async () => {
    if (!selectedGroupId) return;
    try {
      const result = await triggerDryRun({ group_id: selectedGroupId });
      const correlationId = result?.correlation_id;
      if (correlationId) {
        setMergeDryRunCorrelationId(correlationId);
      }
      message.success(AppStrings.KYC_DUPLICATE_IDENTITY_MERGE_DRY_RUN_SUCCESS);
    } catch (error) {
      message.error(
        getApiErrorMessage(
          error,
          AppStrings.KYC_DUPLICATE_IDENTITY_MERGE_DRY_RUN_ERROR,
        ),
      );
      throw error;
    }
  };

  const value = useMemo(
    () => ({
      selectedTab,
      setSelectedTab,
      searchBy,
      setSearchBy,
      searchText,
      setSearchText,
      tenantFilter,
      setTenantFilter,
      pageSize,
      currentPage,
      hasNextPage,
      hasPreviousPage: currentPage > 1,
      goToNextPage,
      goToPreviousPage,
      groups,
      isLoading,
      isFetching,
      isError,
      selectedGroupId,
      openDetail,
      closeDetail,
      detailQuery,
      notesDraft,
      setNotesDraft,
      handleMarkSupportContacted,
      handleEscalate,
      handleIgnore,
      handleMergeDryRun,
      isUpdating,
      isDryRunPending,
      mergeDryRunCorrelationId,
    }),
    [
      selectedTab,
      searchBy,
      searchText,
      tenantFilter,
      pageSize,
      currentPage,
      hasNextPage,
      groups,
      isLoading,
      isFetching,
      isError,
      selectedGroupId,
      detailQuery,
      notesDraft,
      isUpdating,
      isDryRunPending,
      mergeDryRunCorrelationId,
    ],
  );

  return (
    <DuplicateIdentityReviewContext.Provider value={value}>
      {children}
    </DuplicateIdentityReviewContext.Provider>
  );
}

export function useDuplicateIdentityReview() {
  const ctx = useContext(DuplicateIdentityReviewContext);
  if (!ctx) {
    throw new Error(
      "useDuplicateIdentityReview must be used within DuplicateIdentityReviewProvider",
    );
  }
  return ctx;
}

export { GET_DUPLICATE_IDENTITY_GROUPS_QUERY_KEY, ACTIONED_STATUSES };
